import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';

import { Field } from '@base-ui/react/field';
import { Popover } from '@base-ui/react/popover';
import { format, isAfter, isBefore, isValid, parse, startOfDay } from 'date-fns';

import { CalendarBlankIcon, CaretLeftIcon, CaretRightIcon } from '../Icon';
import { FieldLabel } from '../InfoButton';
import { useInheritedDarkMode } from '../Theme';
import { cn, forwardBoxPress } from '../utils';
import {
  datePickerBoxRecipe,
  datePickerCalendarClassNames,
  datePickerControlRecipe,
  datePickerHelperRecipe,
  datePickerLabelRecipe,
  datePickerRequiredMarkRecipe,
  datePickerPopupRecipe,
  datePickerPositionerRecipe,
  datePickerRootRecipe,
  datePickerTriggerRecipe,
} from './DatePicker.recipe';
import { browserDatePickerLocale } from './locale';
import { DATE_PICKER_FORMATS, DEFAULT_DATE_PICKER_MESSAGES } from './types';

import type { FieldInfoProps } from '../InfoButton';
import type { DatePickerAccessibleLabels, DatePickerEntryError, DatePickerProps } from './types';

/**
 * The month chevrons, handed to `react-day-picker` in place of its own.
 *
 * The library renders a bare `<polygon>` with no `fill` attribute, which paints BLACK rather than
 * inheriting — the recipe used to correct that with a `fill-current` utility. The drawing settles
 * it differently: the nav buttons carry `CaretLeft` and `CaretRight`, which exist in the design
 * system already, so the library's glyph is replaced rather than recoloured. Both carry
 * `fill="currentColor"` as an ATTRIBUTE, so the button's `text-` utility reaches them and nothing
 * needs a CSS `fill` rule — which would beat the attribute and paint the glyph blank.
 *
 * Only `left` and `right` can occur: this calendar has no dropdown caption, which is what the
 * other two orientations are for.
 */
const NavChevron = ({
  orientation,
  className,
}: {
  orientation?: 'up' | 'down' | 'left' | 'right';
  className?: string;
}) => (orientation === 'left' ? <CaretLeftIcon className={className} /> : <CaretRightIcon className={className} />);

/**
 * The shape a typed entry has to have before it is worth parsing.
 *
 * `date-fns`' `parse` is forgiving about digit counts — it reads `5/3/2026` as the 5th of March,
 * which is what a user typing quickly means — but it is forgiving in one place this component is
 * not: `05/03/26` parses to the year 26 rather than 2026, silently, and the field would echo back
 * `05/03/0026`. So the year is pinned to four digits here, before `parse` ever sees the string,
 * and the day and month are left loose.
 */
const SHORT_DATE = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

/** The committed value as typed text, which is the empty string for an empty field. */
const toText = (value: Date | null, typedFormat: string) => (value === null ? '' : format(value, typedFormat));

/** Two committed values are the same when they are the same DAY, since that is all this field holds. */
const sameDay = (a: Date | null, b: Date | null) => {
  if (a === null || b === null) {
    return a === b;
  }

  return startOfDay(a).getTime() === startOfDay(b).getTime();
};

/**
 * Reads a non-empty typed entry once, for all three places that need it: committing on blur, the
 * day the calendar shows as chosen, and the month it follows.
 *
 * `parsed` is set whenever the text is a real date, in range or not, because the calendar follows
 * an out-of-range entry to its month. `error` says why the entry would be refused, and is `null`
 * for one that commits.
 */
const parseTyped = (
  entry: string,
  typedFormat: string,
  min: Date | undefined,
  max: Date | undefined
): { parsed: Date | null; error: DatePickerEntryError | null } => {
  if (!SHORT_DATE.test(entry)) {
    return { parsed: null, error: 'format' };
  }

  const parsed = parse(entry, typedFormat, new Date());

  if (!isValid(parsed)) {
    return { parsed: null, error: 'notReal' };
  }

  if (min !== undefined && isBefore(parsed, startOfDay(min))) {
    return { parsed, error: 'beforeMin' };
  }

  if (max !== undefined && isAfter(parsed, startOfDay(max))) {
    return { parsed, error: 'afterMax' };
  }

  return { parsed, error: null };
};

/**
 * English defaults for the two names only a screen reader hears, replaceable one at a time
 * through `accessibleLabels`.
 *
 * `trigger` CHANGES with the committed value, which is the half of the open/close cycle a keyboard
 * user actually perceives: focus returns to the button when the calendar closes, and a static
 * "Choose date" would announce nothing about what just happened. The date is spelled out in full
 * in the field's own reading order, because a screen reader reading `05/03/2026` aloud is exactly
 * the ambiguity `DATE_PICKER_FORMATS` documents.
 *
 * Both take the field's `label`, so one shared set can be written once and passed to every date
 * field on a screen rather than rebuilt per call site.
 */
const defaultAccessibleLabels = (spokenFormat: string): DatePickerAccessibleLabels => ({
  trigger: (value, label) =>
    value === null ? `Choose a date for ${label}` : `Change the date for ${label}, ${format(value, spokenFormat)}`,
  calendar: (label) => `${label} calendar`,
});

/*
 * Design System 2.0 date picker — a field that takes a date by typing or from a calendar.
 *
 * Ported 2026-09-17 from `Date Picker` 1438:129727, with its two internal sets `.Date Picker_Day`
 * 1438:130280 and `Date Picker_Calendar` 1438:130281. This header used to say the library
 * contained no date picker in any state; it does now, and the set was drawn FROM this file, so
 * most of the port was confirmation. The field half restates the text-input family's geometry and
 * colours so the field types stack in one group without a seam — note it now follows `TextInput`,
 * which has been through the same port, rather than `TextArea`, which has not. Read the
 * `.ds-datepicker` block in `design-system.css` and the header of `DatePicker.recipe` before
 * changing anything visual: between them they list every value the port moved and the one
 * treatment that deliberately still differs from the drawing.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer —
 * including for the portalled calendar, which gets there via `useInheritedDarkMode`, the same hook
 * `Dialog`, `Select` and `Menu` use.
 *
 * `position` is normally supplied by `FieldGroup` rather than set by hand.
 *
 * ── Why the calendar is `react-day-picker` and not hand-rolled ────────────────────────────────
 *
 * A month grid's keyboard model is the part of this component that is easy to get wrong and hard
 * to notice: arrows for a day, Up/Down for a week, Home/End for the visible week, PageUp/PageDown
 * for a month, Shift+Page for a year, one tab stop for the whole grid, and focus landing on the
 * selected day or on today when it opens. The library implements all of it, and
 * `DatePicker.test.tsx` asserts each key rather than trusting it — including the rendered ARIA,
 * because that is what a library upgrade is most likely to change underneath this file.
 *
 * ── Typed entry is this component's own, and its contract is exact ────────────────────────────
 *
 * The library ships no input, so everything below the calendar is hand-rolled and the rules are
 * worth stating in one place. The typed format is the locale's `typed` entry in
 * `DATE_PICKER_FORMATS` and nothing else; see the note beside it for why a committed date is
 * spelled out and where the locale comes from.
 *
 *   - Parsing happens on BLUR, never per keystroke. A field that went red on the second character
 *     of every date would be reporting a state the user is still in the middle of leaving.
 *   - An entry that parses and is in range is committed, and the field shows it spelled out — so
 *     `5/3/2026` becomes `Thu 5 Mar 2026` and the user can see what was understood. Focusing the
 *     field again brings back the typed form, `05/03/2026`, for editing.
 *   - An entry that does NOT parse, or that falls outside `min`/`max`, leaves the committed value
 *     untouched and puts the field in its invalid state. It deliberately does not revert the text:
 *     silently replacing what someone typed with the previous value is the behaviour that makes a
 *     date field feel broken, and it also destroys the evidence of what went wrong.
 *   - Clearing the field commits `null`. An empty field is a value, not an error.
 *   - `onValueChange` fires only when the committed DAY actually moves, so a blur with no edit
 *     behind it reports nothing.
 *
 * The component's own invalid state is ORed with the caller's `error`, so the two cannot cancel
 * each other out: a caller clearing its flag does not clear a parse failure the user has not
 * fixed, and vice versa.
 */
/**
 * Choose one calendar day or type it: `dd/MM/yyyy` for `locale="en-GB"`, `MM/dd/yyyy` for
 * `locale="en-US"`. The default locale comes from the browser's time zone. Parsing happens on blur;
 * a committed date is shown spelled out (`Thu 5 Mar 2026` / `Thu, Mar 5, 2026`) and returns to the
 * typed form on focus. An invalid or out-of-range entry keeps the committed value unchanged and
 * does not call `onValueChange`. `min` and `max` are inclusive. Clearing the field commits `null`.
 * Use separate fields for a range; time of day is not supported.
 *
 * `accessibleLabels` overrides screen-reader names. Its optional callbacks are
 * `trigger(value: Date | null, label: string)` for the calendar button and
 * `calendar(label: string)` for the popup. For example:
 *
 * ```tsx
 * <DatePicker label="Start date" accessibleLabels={{
 *   trigger: (value, label) => value
 *     ? `Change ${label}, ${value.toLocaleDateString('en-GB', { dateStyle: 'long' })}`
 *     : `Choose ${label}`,
 *   calendar: (label) => `${label} calendar`,
 * }} />
 * ```
 *
 * Use the app's translated wording and locale in these callbacks.
 *
 * `FieldGroup` normally supplies `position` when this field stacks with other inputs.
 * Optional `info` and `infoLabel` travel together for extra context beside the label; put
 * instructions needed to complete the field in always-visible `helperText` instead.
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  {
    label,
    value,
    defaultValue,
    onValueChange,
    onBlur,
    min,
    max,
    locale: localeProp,
    helperText,
    messages,
    error,
    disabled,
    info,
    infoLabel,
    accessibleLabels,
    name,
    required,
    position,
    className,
    rootClassName,
    popupClassName,
    open,
    defaultOpen,
    onOpenChange,
    today,
    'data-testid': testId,
  },
  ref
) {
  // Re-paired after destructuring: `FieldLabel` takes the discriminated pair, not two optionals.
  const fieldInfo: FieldInfoProps = info && infoLabel ? { info, infoLabel } : {};
  const locale = localeProp ?? browserDatePickerLocale();
  const formats = DATE_PICKER_FORMATS[locale];

  /**
   * The bordered box, held in STATE rather than in a ref.
   *
   * Both consumers below need the node during a render, and a ref is empty on the one render that
   * matters most: a field mounted with the calendar already open reads `null` for the theme
   * sentinel and resolves light inside a dark region, with nothing to force the second render that
   * would correct it. A state-backed ref callback re-renders when the node attaches, so the
   * sentinel and the anchor are both live by the time the portal resolves.
   */
  const [box, setBox] = useState<HTMLDivElement | null>(null);

  const [uncontrolledValue, setUncontrolledValue] = useState<Date | null>(defaultValue ?? null);
  const committed = value === undefined ? uncontrolledValue : value;

  const [text, setText] = useState(() => toText(committed, formats.typed));
  const [entryError, setEntryError] = useState<DatePickerEntryError | null>(null);
  const typedInvalid = entryError !== null;
  const [focused, setFocused] = useState(false);

  /**
   * How the calendar was opened, which decides where focus goes.
   *
   * From the field, the person is typing: focus stays in the input, the calendar follows what they
   * type, and ArrowDown is how they step into the grid. From the button — or opened from outside, as
   * a story or a test does — the grid takes focus, as it always has. Held in state so the
   * `autoFocus` the calendar mounts with reads the value of THIS opening.
   */
  const [openedBy, setOpenedBy] = useState<'field' | 'button'>('button');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  // The caller's ref still reaches the real <input>; the component keeps its own handle to it as well.
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);
  const [month, setMonth] = useState<Date>(() => committed ?? today ?? new Date());

  /**
   * The committed value as of the last render, kept so a change made from OUTSIDE can be noticed.
   *
   * Derived during render rather than in an effect, which is the documented React pattern for
   * "reset some state when a prop changes". An effect would paint one frame of the previous text
   * against the new value, and on a controlled field that frame is visible every time a form
   * resets. The comparison is by day rather than by reference because a parent that rebuilds its
   * `Date` on every render would otherwise wipe the user's in-progress typing on each keystroke
   * elsewhere on the page.
   */
  const [seen, setSeen] = useState<Date | null>(committed);
  const [seenLocale, setSeenLocale] = useState(locale);

  if (!sameDay(seen, committed) || seenLocale !== locale) {
    setSeen(committed);
    setSeenLocale(locale);
    setText(toText(committed, formats.typed));
    setEntryError(null);
  }

  /**
   * What the input shows: the spelled-out date at rest, the typed text while focused.
   *
   * Only when the text still IS the committed date — a rejected entry stays on screen as typed,
   * because it is the evidence of what went wrong.
   */
  const shown =
    !focused && !typedInvalid && committed !== null && text === toText(committed, formats.typed)
      ? format(committed, formats.display)
      : text;

  const [uncontrolledOpen, setUncontrolledOpen] = useState(Boolean(defaultOpen));
  const isOpen = open ?? uncontrolledOpen;

  /**
   * The calendar is PORTALLED into `<body>`, so it has none of the field's ancestors and the token
   * layer — which resolves colour from position in the DOM — would open a light calendar on a dark
   * page. The box is a real node at this component's place in the tree, so it doubles as the theme
   * sentinel exactly as `Menu`'s anchor does, and no hidden sentinel element is needed.
   */
  const { inheritedDarkClass } = useInheritedDarkMode(isOpen, box);

  const commit = useCallback(
    (next: Date | null) => {
      setUncontrolledValue(next);
      setSeen(next);
      setText(toText(next, formats.typed));
      setEntryError(null);

      if (next !== null) {
        setMonth(next);
      }

      // Only a CHANGE is worth reporting. Everything above runs either way — re-typing the date
      // that is already committed still has to canonicalise the text and clear a parse failure —
      // but focusing a field and leaving it without an edit is not a value change, and a caller
      // that marks its form dirty on every notification would say so on a blur nobody typed into.
      if (!sameDay(committed, next)) {
        onValueChange?.(next);
      }
    },
    [committed, formats.typed, onValueChange]
  );

  /**
   * The whole of the parse contract, run once, on blur.
   *
   * The range check compares against the START OF DAY of `min`/`max` rather than the instants
   * themselves, so that a mid-day boundary means the whole of its day. That is not a softening of
   * the caller's bound — it is what the grid already does: `react-day-picker` matches `before` and
   * `after` with `differenceInCalendarDays`, which ignores time of day entirely. Compared against
   * the raw instants, a `min` of 14:00 would reject a typed `05/03/2026` (parsed at midnight)
   * while the calendar happily offered the 5th, and the two ways into this field would disagree.
   */
  const settle = useCallback(() => {
    const entry = text.trim();

    if (entry === '') {
      commit(null);

      return null;
    }

    const { parsed, error: refusal } = parseTyped(entry, formats.typed, min, max);

    if (refusal !== null || parsed === null) {
      setEntryError(refusal);

      return refusal;
    }

    commit(startOfDay(parsed));

    return null;
  }, [commit, formats.typed, max, min, text]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean, details?: { reason?: string; event?: Event }) => {
      // A press inside the field is not "outside" the calendar: the field is what opens it, and
      // closing on the same press would make a second click flicker it shut and open.
      if (
        !nextOpen &&
        details?.reason === 'outside-press' &&
        box !== null &&
        details.event?.target instanceof Node &&
        box.contains(details.event.target)
      ) {
        return;
      }

      // Base UI also closes the calendar when focus moves on, and treats the button beside the value
      // as "moving on" because the input is not the element it anchors to. Focus that is still inside
      // the field or the calendar is not leaving, so that close is ignored.
      if (!nextOpen && details?.reason === 'focus-out') {
        const target = (details.event as FocusEvent | undefined)?.relatedTarget;

        if (target instanceof Node && (box?.contains(target) || popupRef.current?.contains(target))) {
          return;
        }
      }

      if (nextOpen) {
        setMonth(committed ?? today ?? new Date());
      }

      setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [box, committed, onOpenChange, today]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      // Fired first and unconditionally, because a blur happened whatever the typed entry parses
      // to. A caller using this to mark a field touched must not have that depend on the parse.
      onBlur?.();
      setFocused(false);
      settle();

      // Focus going into the calendar, or onto the button beside the value, keeps it open. Going anywhere else closes it, and must not be
      // undone by the focus restore that closing normally does, or Tab would bounce back here.
      const next = event.relatedTarget;

      const staysInside = next instanceof Node && (popupRef.current?.contains(next) || box?.contains(next));

      if (isOpen && !staysInside) {
        handleOpenChange(false);
      }
    },
    [box, handleOpenChange, isOpen, onBlur, settle]
  );

  const handleSelect = useCallback(
    (selected: Date | undefined) => {
      commit(selected === undefined ? null : startOfDay(selected));
      handleOpenChange(false);

      if (openedBy === 'field') {
        inputRef.current?.focus();
      }
    },
    [commit, handleOpenChange, openedBy]
  );

  const labelFor = { ...defaultAccessibleLabels(formats.spoken), ...accessibleLabels };
  const sentences = { ...DEFAULT_DATE_PICKER_MESSAGES, ...messages };

  // What the calendar shows as chosen while a date is being typed: a complete, real, in-range entry.
  const typed = parseTyped(text.trim(), formats.typed, min, max);
  const typedDate = typed.error === null && typed.parsed !== null ? startOfDay(typed.parsed) : null;

  // A `beforeMin` or `afterMax` entry error whose bound has since been removed has no sentence.
  const entryMessage =
    entryError === 'format'
      ? sentences.format(formats.placeholder, format(new Date(2026, 2, 5), formats.typed))
      : entryError === 'notReal'
        ? sentences.notReal
        : entryError === 'beforeMin' && min !== undefined
          ? sentences.beforeMin(format(min, formats.display))
          : entryError === 'afterMax' && max !== undefined
            ? sentences.afterMax(format(max, formats.display))
            : null;
  const shownHelper = entryMessage ?? helperText;
  const hasHelper = Boolean(shownHelper);

  return (
    /**
     * Base UI's open state is CONTROLLED from here even when the caller leaves `open` unset, and
     * that is load-bearing rather than tidiness: choosing a day has to close the calendar, and a
     * popover managing its own state cannot be closed from outside it. `uncontrolledOpen` is the
     * fallback the caller would otherwise have got, so an uncontrolled `DatePicker` behaves
     * exactly as one would expect — `Select` keeps its own open state for the same reason, there
     * to re-resolve the portal's theme.
     */
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      {/* `invalid` is what puts `data-invalid` on the root and `aria-invalid` on the control; every
          red token below keys off it, so the visual and the announced state cannot drift apart. */}
      <Field.Root
        className={cn(datePickerRootRecipe(), rootClassName)}
        data-testid={testId}
        disabled={disabled}
        invalid={Boolean(error) || typedInvalid}
      >
        {/* `data-part` names the bordered box so a parent can target it and a test can assert on it
            without depending on a styling class. The slot classes sit on the root. */}
        <div
          ref={setBox}
          className={cn(datePickerBoxRecipe({ position, hasHelper }), className)}
          data-part="box"
          onClick={(event) => {
            // A click anywhere in the field opens the calendar; the button keeps toggling it. Already
            // open, there is nothing to do: opening again would reset the month the calendar followed
            // the typing to.
            if (!disabled && !isOpen && !(event.target as HTMLElement).closest('button')) {
              setOpenedBy('field');
              handleOpenChange(true);
            }
          }}
          onMouseDown={forwardBoxPress}
        >
          {/* The control is the FIRST child and pays all the padding itself, so the input fills the
              box and the entire field is a click target — see `DatePicker.recipe`. */}
          <Field.Control
            ref={inputRef}
            autoComplete="off"
            className={datePickerControlRecipe({ hasHelper })}
            // Numeric rather than `numeric`-only `type`: a native `<input type="date">` would bring
            // its own picker and its own parser, which is the whole thing this component replaces.
            // `inputMode` still gets a phone keyboard to the digits without any of that.
            inputMode="numeric"
            name={name}
            placeholder={formats.placeholder}
            required={required}
            value={shown}
            onBlur={handleBlur}
            onChange={(event) => {
              const next = event.target.value;

              setText(next);

              // The calendar follows a date as it is typed, so the month on screen is the month
              // being typed rather than the one it opened on.
              const { parsed } = parseTyped(next.trim(), formats.typed, min, max);

              if (parsed !== null) {
                setMonth(parsed);
              }
            }}
            onFocus={() => setFocused(true)}
            onKeyDown={(event) => {
              if (event.key === 'Tab' && isOpen) {
                // Tab is leaving the field, so the calendar goes with it. Forward, focus is moved by
                // hand: Base UI keeps focus-guard elements next to the open calendar, and tabbing
                // onto one throws focus straight back into the input.
                handleOpenChange(false);

                if (!event.shiftKey) {
                  event.preventDefault();
                  box?.querySelector('button')?.focus();
                }
              } else if (event.key === 'ArrowDown') {
                event.preventDefault();

                if (!isOpen) {
                  setOpenedBy('field');
                  handleOpenChange(true);
                } else {
                  popupRef.current?.querySelector<HTMLButtonElement>('button[tabindex="0"]')?.focus();
                }
              } else if (event.key === 'Enter' && isOpen) {
                // Settles the typed entry and closes, instead of submitting a form that happens to
                // contain the field while the calendar is up.
                event.preventDefault();

                if (settle() === null) {
                  handleOpenChange(false);
                }
              }
            }}
          />

          {/* Always drawn. This field floats its label rather than hiding it once filled — see the
              note in `Inputs/DatePicker/toDsProps` for the version that hid it and why that was a
              defect rather than a feature. */}
          <FieldLabel
            {...fieldInfo}
            className={datePickerLabelRecipe()}
            disabled={disabled}
            label={label}
            required={required}
            requiredMarkClassName={datePickerRequiredMarkRecipe()}
          />

          <Popover.Trigger
            aria-label={labelFor.trigger(committed, label)}
            className={datePickerTriggerRecipe()}
            disabled={disabled}
            onClick={() => setOpenedBy('button')}
          >
            <CalendarBlankIcon />
          </Popover.Trigger>

          {/**
           * Helper text renders inside the border, which is what keeps this field stackable — see
           * `datePickerHelperRecipe`.
           *
           * `Field.Error` needs `match` to render off a caller-supplied flag; without it, it renders
           * only for Base UI's own constraint validation, which this component does not drive.
           * Neither part is a live region — that is Base UI's behaviour rather than a choice made
           * here — so an error that appears after first paint is correctly ASSOCIATED immediately
           * but is not necessarily spoken until the field is next visited. The other two fields
           * have exactly the same property.
           */}
          {hasHelper && (Boolean(error) || typedInvalid) && (
            <Field.Error aria-live="polite" className={datePickerHelperRecipe()} match>
              {shownHelper}
            </Field.Error>
          )}
          {hasHelper && !(Boolean(error) || typedInvalid) && (
            <Field.Description className={datePickerHelperRecipe()}>{shownHelper}</Field.Description>
          )}
        </div>
      </Field.Root>

      {/* The theme class goes on the PORTAL, which is an ancestor of the popup — so the popup
          carries only its own `ds-datepicker` slot class and the ordinary cascade reaches it,
          exactly as for a calendar that never portalled. */}
      <Popover.Portal className={inheritedDarkClass}>
        <Popover.Positioner
          align="start"
          // Anchored to the BOX rather than to the 24px button inside it, so the calendar lines up
          // with the field a user is looking at rather than with a glyph in its corner.
          anchor={box}
          className={datePickerPositionerRecipe()}
          side="bottom"
          /**
           * 4px of air between the field and the calendar.
           *
           * This was 8, copied from `Menu` and `Select`. The drawing places the calendar as an
           * absolutely-positioned child of the field at the field's own start edge, 4px below the
           * box, and 4 was confirmed as the intended distance on 2026-09-17.
           *
           * The disagreement this note used to flag is resolved on both sides. The set's cells
           * claimed 4 and actually measured 3; all 16 `isOpen=Yes` cells were moved to a true 4 in
           * the same pass as this change, so the drawing and the code now agree.
           *
           * Select and Menu now use the same 4px edge gap, so the three popup families remain in
           * step when collision handling flips them above their anchors.
           */
          sideOffset={4}
        >
          <Popover.Popup
            ref={popupRef}
            aria-label={labelFor.calendar(label)}
            className={cn(datePickerPopupRecipe(), popupClassName)}
            data-part="calendar"
            // Base UI would restore focus to the element that had it before opening, which drags a
            // Tab back into the field. For a calendar opened from the field, focus is restored by hand
            // where it is wanted: after a day is picked, and on Escape.
            finalFocus={openedBy === 'field' ? false : undefined}
            // Clicking the calendar must not pull focus out of the field mid-typing.
            onKeyDown={(event) => {
              if (event.key === 'Escape' && openedBy === 'field') {
                inputRef.current?.focus();
              }
            }}
            onMouseDown={(event) => {
              if (openedBy === 'field') {
                event.preventDefault();
              }
            }}
            /**
             * Base UI's default would focus the first tabbable element in the popup, which is the
             * previous-month button. APG asks for the selected day, or today when nothing is
             * selected. So Base UI is told to move no focus at all and the library's own
             * `autoFocus` does it instead — it marks the right day as focused, and the library's
             * day button focuses itself when that flag lands on it.
             *
             * The SETTLED position is the same either way, because the library's effect runs last
             * and wins; this prevents the intermediate move, which a screen reader announces and
             * no assertion on the settled state can catch. See `OpeningTheCalendar`.
             */
            initialFocus={false}
          >
            <DayPicker
              autoFocus={openedBy === 'button'}
              showOutsideDays
              classNames={datePickerCalendarClassNames}
              components={{ Chevron: NavChevron }}
              month={month}
              onMonthChange={setMonth}
              disabled={[
                ...(min === undefined ? [] : [{ before: min }]),
                ...(max === undefined ? [] : [{ after: max }]),
              ]}
              endMonth={max}
              // A fixed six-week grid, so the calendar does not change height between a 4-row
              // February and a 6-row month — which on a popover anchored below the field would move
              // the whole surface under the pointer mid-navigation.
              fixedWeeks
              mode="single"
              selected={typedDate ?? committed ?? undefined}
              startMonth={min}
              today={today}
              weekStartsOn={formats.weekStartsOn}
              onSelect={handleSelect}
            />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
});
